import React from "react"
import PropTypes from "prop-types"
import Fuse from "fuse.js"
import LayerMenu from "./LayerMenu"
import styled from "styled-components"
import calculateDistance from "../../../utils/calculateDistance"
import Map from "./map"
import { toast } from "react-toastify"
import { Spacer, Button, Pharmacy } from "@bw/bits"
import { Columns } from "@bw/modules"
import { Checkbox, FormField, Input } from "@bw/forms"
import { Formik, Form, Field } from "formik"
import { useTranslation } from "react-i18next"
import { breakpoints } from "../../../theme"
import * as Icons from "@bw/icons"

const Mobile = styled.div`
  display: flex;
  justify-content: end;

  @media (min-width: ${breakpoints.small}px) {
    display: none;
  }
`

const Desktop = styled.div`
  display: none;

  @media (min-width: ${breakpoints.small}px) {
    display: block;
  }
`

const Overflow = styled.div`
  max-height: 360px;

  overflow: auto;
  ::-webkit-scrollbar {
    width: 3px;
  }
  ::-webkit-scrollbar-track {
    background: #ebf4ea;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--page-color);
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--page-color);
  }

  @media (min-width: ${breakpoints.large}px) {
    ${props => (props.map ? "padding: 0 75px;" : null)}
  }
`

const initialValues = {
  city: "",
  canton: [],
  position: null,
}

let timer

const SearchPharmacy = ({ map, textSearch, state, pharmacies }) => {
  const { t } = useTranslation()
  const [show, setShow] = React.useState(false)
  const [filter, setFilter] = React.useState(initialValues)

  const fuse = React.useMemo(() => {
    if (!textSearch) {
      return null
    }
    return new Fuse(pharmacies, {
      keys: [
        {
          name: "title",
          weight: 3,
        },
        {
          name: "city",
          weight: 4,
        },
        {
          name: "zipCode",
          weight: 4,
        },
      ],
      minMatchCharLength: 3,
      ignoreLocation: true,
      threshold: 0.2,
    })
  }, [pharmacies, textSearch])

  const filteredPharmacies = React.useMemo(() => {
    let filtered = pharmacies

    /**
     * Filter by searchtext
     */
    if (textSearch && fuse && filter.city.length > 2) {
      filtered = fuse.search(filter.city).map(result => result.item)
    }

    /**
     * Filter by canton
     */
    if (state && filter.canton.length > 0) {
      filtered = filtered.filter(pharmacy =>
        filter.canton.includes(pharmacy.state)
      )
    }

    /**
     * Filter by position
     */
    if (map && filter.position !== null) {
      filtered = filtered.map(point => ({
        ...point,
        distance: calculateDistance(
          filter.position.latitude,
          filter.position.longitude,
          point.latitude,
          point.longitude
        ),
      }))

      filtered = filtered.filter(
        pharmacy => pharmacy.distance <= 20 // radius in km
      )
      filtered.sort((a, b) => a.distance - b.distance)
    }

    if (filtered.length === 0) {
      if (filter.position !== null) {
        setFilter({ ...filter, position: initialValues.position })
        toast.error(t("toast.noGeoResult"))
      } else {
        toast.error(t("toast.noResult"))
      }
    }

    return filtered
  }, [pharmacies, filter, t, fuse, map, state, textSearch])

  return (
    <Spacer gap="large">
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          setFilter({
            ...filter,
            ...values,
          })
        }}
      >
        {({ submitForm, handleReset }) => (
          <Form
            onChange={e => {
              if (e.target.name === "city") {
                clearTimeout(timer)
                timer = setTimeout(submitForm, 500)
              } else {
                submitForm()
              }
            }}
          >
            <Desktop>
              <Columns count={3} gap="xxlarge">
                <Filters
                  {...{
                    filter,
                    setFilter,
                    handleReset,
                    map,
                    textSearch,
                    state,
                  }}
                />
              </Columns>
            </Desktop>
            <Mobile>
              <Button
                type="button"
                label={t("form.buttonShowFilters")}
                tabIndex="0"
                onKeyDown={() => {
                  setShow(true)
                }}
                onClick={e => {
                  e.preventDefault()
                  setShow(true)
                  return false
                }}
              />
              <LayerMenu show={show} setShow={setShow}>
                <h3>Filtres</h3>
                <Columns count={3}>
                  <Filters
                    {...{
                      filter,
                      setFilter,
                      handleReset,
                      map,
                      textSearch,
                      state,
                    }}
                  />
                </Columns>
              </LayerMenu>
            </Mobile>
          </Form>
        )}
      </Formik>
      {map && <Map allPois={pharmacies} filteredPois={filteredPharmacies} />}
      <Overflow {...{ map }}>
        <Columns count={3} gap="xlarge">
          {filteredPharmacies.map((item, i) => (
            <Pharmacy
              key={i}
              pharmacyTitle={item.title}
              pharmacyLink={item.slug}
              street={item.street}
              npa={item.zipCode}
              city={item.city}
              distance={item.distance}
              showItinerary
            />
          ))}
        </Columns>
      </Overflow>
    </Spacer>
  )
}

const Filters = ({
  map,
  textSearch,
  state,
  filter,
  setFilter,
  handleReset,
}) => {
  const { t } = useTranslation()

  return (
    <>
      {textSearch && (
        <FormField title={t("form.city")} name="city">
          <Field name="city" component={Input} autoComplete="off" />
        </FormField>
      )}
      {state && (
        <FormField title={t("form.canton")} name="canton">
          <Spacer direction="row">
            {["be", "fr", "ge", "ju", "ne", "ti", "vd", "vs"].map(state => (
              <Field
                key={state}
                value={state}
                name="canton"
                component={Checkbox}
                title={state.toUpperCase()}
                checked={filter.canton.includes(state)}
              />
            ))}
          </Spacer>
        </FormField>
      )}
      {map && typeof navigator !== "undefined" && navigator.geolocation && (
        <FormField title={t("form.poximity")} name="proximity">
          <Button
            label={t("form.buttonProximity")}
            icon={<Icons.Geolocalize />}
            onClick={() => {
              handleReset()
              navigator.geolocation.getCurrentPosition(
                position => {
                  setFilter({ ...initialValues, position: position.coords })
                },
                error => {
                  // check if the user denied geolocation, or if there was any other problem
                  if (error.code === error.PERMISSION_DENIED) {
                    toast.error(
                      "La géolocalisation a été bloquée sur cette page"
                    )
                  } else {
                    toast.error("Impossible de trouver votre position.")
                  }
                },
                {
                  enableHighAccuracy: true,
                  timeout: 10000,
                }
              )
            }}
          />
        </FormField>
      )}
    </>
  )
}

SearchPharmacy.propTypes = {
  map: PropTypes.bool,
  textSearch: PropTypes.bool,
  states: PropTypes.bool,
  pharmacies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      street: PropTypes.string.isRequired,
      zipCode: PropTypes.number.isRequired,
      city: PropTypes.string.isRequired,
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    })
  ),
}

SearchPharmacy.defaultProps = {
  map: true,
  textSearch: true,
  state: true,
}

export { SearchPharmacy }
